import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import H2 from '@base/H2/H2';
import P1 from '@base/P1/P1';
import H3 from '@base/H3/H3';
import ContentsTable from '@base/ContentsTable/ContentsTable';
import MenusNav from '@base/MenusNav/MenusNav';
import { menus } from '@utils/constants';
import { Spacings } from '@styles/index';
import Layout from '@layout/index';
import LeftContainer from '@cont/CompanyDetails/LeftContainer';
import RightContainer from '@cont/CompanyDetails/RightContainer';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: span 24;
`;

const StyledH3 = styled(H3)`
  margin-bottom: ${Spacings.SPACING_6B};
  padding-top: 100px;
  margin-top: -100px;
`;

const Paragraph = styled(P1)`
  margin-bottom: ${Spacings.SPACING_4B};
  text-align: justify;
`;

const StyledParagraph = styled(Paragraph)`
  margin-bottom: ${Spacings.SPACING_4B};
  ol > li {
    margin-bottom: ${Spacings.SPACING_2B};
    &:first-of-type {
      margin-top: ${Spacings.SPACING_2B};
    }
  }
`;

const ListItem = ({ children }) => (
  <StyledParagraph as="li">{children}</StyledParagraph>
);

const tableData = [
  {
    label: 'A. Subscription & Eligibility',
    id: 'subscription_and_eligibility'
  },
  { label: 'B. Compatible Devices', id: 'compatible_devices' },
  { label: 'C. Live Mentorship Services', id: 'live_mentorship_services' },
  { label: 'D. Additional Terms', id: 'additional_terms' }
];

const LiveMentorshipTerms = () => {
  const router = useRouter();
  const { pathname } = router;

  return (
    <>
      <LeftContainer>
        <MenusNav data={menus} currentHref={pathname} />
      </LeftContainer>

      <RightContainer>
        <Wrapper>
          <div>
            <div>
              <H2 as="h1">Unacademy Live Mentorship Terms &amp; Conditions</H2>
            </div>
            <br />
            <br />
            <ContentsTable data={tableData} />
            <Paragraph>
              Welcome to the terms of use for Unacademy Live Mentorship. These
              terms are between you and Sorting Hat Technologies Private Limited
              (“Unacademy”, “we” or “us”). Please read these terms, along with
              the Privacy Policy, Terms of Use, and all other rules and policies
              made available or published on Unacademy platform or mobile
              application (including, but not limited to, any rules or usage
              provisions specified on any page or on any help or other
              informational page on Unacademy website or mobile application)
              (collectively, this “Agreement”). The versions of the
              above-mentioned policies and terms are available on Unacademy
              website and/or mobile application for your reference and to
              understand how we handle your personal information. Every time you
              visit, browse, or use your Unacademy Subscription, you accept this
              Agreement.
            </Paragraph>
            <br />
            <br />
            <a name={tableData[0].id}>
              <StyledH3>{tableData[0].label}</StyledH3>
            </a>
            <Paragraph>
              Unacademy Live Mentorship (the “Live Mentorship Service”) is a
              personalized service that offers live mentorship services to
              Learners with Iconic Subscription through which the Iconic
              Learners can book an audio session with mentors for doubts
              clearing, strategy and study plan or for concept clarity. You will
              be given access to the Live Mentorship Service, through the
              Unacademy website or Unacademy mobile application (as applicable)
              by taking Iconic Subscription to any category of your choice from
              the available iconic subscription plans (“Subscription”).
              Depending on the duration of your iconic subscription you shall be
              allotted live mentorship sessions that you can book with the
              mentors for doubts clearing, strategy and study plan or for
              concept clarity during your Iconic Subscription Period. If you are
              under 18 years of age, or the age of majority in your location,
              you may use these Live Mentorship Services only with involvement
              of a parent or guardian.
            </Paragraph>
            <br />
            <br />

            <a name={tableData[1].id}>
              <StyledH3>{tableData[1].label}</StyledH3>
            </a>
            <Paragraph>
              In order to avail the Live Mentorship Service, you will need to
              use a personal computer, portable media player, or other device
              that meets the system and compatibility requirements (a
              “Compatible Device”). The Compatible Devices may change from time
              to time and, in some cases, whether a device is (or remains) a
              Compatible Device may depend on software or systems provided or
              maintained by the device manufacturer or other third parties.
              Accordingly, devices that are Compatible Devices at one time may
              cease to be Compatible Devices in the future. Thus, kindly make
              sure that the device that you use is compatible with our
              system/software to avail the Live Mentorship Service.
            </Paragraph>

            <br />
            <br />

            <a name={tableData[2].id}>
              <StyledH3>{tableData[2].label}</StyledH3>
            </a>
            <ol>
              <ListItem>
                The Live Mentorship Service allows you to book mentorship
                sessions with the mentors for doubts clearing, strategy and
                study plan or for concept clarity that will help you in your
                preparation, for a limited period of time during the Iconic
                Subscription Period.
              </ListItem>
              <ListItem>
                <b>Usage Rules: </b>Depending on your Iconic Subscription
                duration you shall be allotted a certain number of Live
                Mentorship sessions that you book before the expiry of your
                Iconic Subscription. If due to any reason you have not been able
                to utilize all your allotted Live Mentorship Session then the
                same shall lapse on expiry of your Iconic Subscription. The
                following are restrictions on Unacademy Live Mentorship:
                <ol type="i">
                  <ListItem>
                    <b>Live Mentorship session: </b>A live mentorship session
                    with the mentor shall be for a period of 8 minutes and the
                    same can be extended by the mentor for further 2 minutes at
                    the sole discretion of the mentor and shall be recorded for
                    future revision. You can use the live mentorship session for
                    doubts clearing, strategy and study plan or for concept
                    clarity. The session will be an audio session with a white
                    board which the mentor may use for explaining the concept,
                    doubt or strategy.
                  </ListItem>
                  <ListItem>
                    <b>Downloading: </b>You shall only have the option to do
                    in-app download, if applicable, and the downloaded live
                    mentorship session can be viewed by you during your Iconic
                    Subscription Period in your compatible devices. After
                    downloading the live mentorship session, you shall have
                    access to the same for the Iconic Subscription Period only
                    and all access to the downloaded sessions shall be removed
                    on expiry or termination of your Iconic Subscription, as the
                    case maybe.
                  </ListItem>
                  <ListItem>
                    <b>Viewing Period: </b>You can watch and re-watch the live
                    mentorship session later during your Iconic Subscription
                    Period, by in-app downloading (if applicable) or streaming
                    it, as often as you want and as long as you want provided
                    that you have an active iconic subscription and the viewing
                    is within the Iconic Subscription Period or for a period of
                    time as provided in this Agreement.
                  </ListItem>
                </ol>
              </ListItem>
              <ListItem>
                <b>Booking of Live Mentorship Session: </b>You can book a live
                mentorship session by selecting the time and date on the booking
                page. If it is your first booking then the system will
                auto-assign a mentor to you. However, for all your subsequent
                bookings you shall have the option to choose from the mentors
                with whom you have already attended a live mentorship session or
                you can let the system auto-assign a mentor for you.
              </ListItem>
              <ListItem>
                <b>Cancellation of Live Mentorship Session: </b>If you have
                booked a session but due to some unforeseeable reason have to
                cancel the live mentorship session that was booked by you, you
                can send a cancellation request before 60 mins of the beginning
                of the session. Any request raised within 60 mins of the start
                of the session or anytime communicated on the booking page will
                not be considered and the session shall be considered as
                availed.
              </ListItem>
              <ListItem>
                <b>No Show for Live Mentorship Session: </b>Mentor shall wait
                for a maximum of 2 minutes after starting the call if you do not
                show up within the said time, it will be considered as no show
                on your part and the session shall be considered as availed by
                you and deducted from you allotted live sessions. However, if
                the mentor fails to join the session before completion of 2
                minutes of the session starting then you can cancel the session
                and the same shall be credited back into your account and you
                can rebook a fresh session with the same mentor or with any
                other mentor, in accordance with the terms of this Agreement.
              </ListItem>
              <ListItem>
                <b>Re-credit of the session: </b>There shall be no re-credit of
                the session in case of your last-minute cancellation, no show or
                any network or any other issue at your end. The sole remedy for
                cancellation, no-show or technical issues at our end or at the
                mentor’s end would be crediting the session back to your
                account. Your Live mentorship sessions shall lapse on expiry of
                your Iconic Subscription and there shall be no carry forward in
                case you renew your Iconic Subscription.
              </ListItem>
              <ListItem>
                <b>Recharge of Live Mentorship Services &amp; Payment: </b>If
                applicable, you may recharge your live mentorship session in
                accordance with the plans by clicking the option of your choice
                and making the required payment for the live mentorship session
                recharge plan of your choice to continue availing the Live
                Mentorship Service.
              </ListItem>
              <ListItem>
                <b>Limited License to Live Mentorship Sessions: </b>Subject to
                payment of the Iconic Subscription fee or fee for recharge of
                your live mentorship session, if applicable, and your compliance
                with all terms of this Agreement, Unacademy grants you a
                non-exclusive, non-transferable, non-sublicensable, limited
                license, during the term of your Iconic Subscription Period, to
                access and view and book live mentorship sessions in accordance
                with the terms of this Agreement, for personal, non-commercial,
                private use only. We may automatically remove your access to the
                Live Mentorship Service from your Compatible Device after the
                end of the Iconic Subscription Period or in accordance with any
                restriction mentioned in this Agreement.
              </ListItem>
              <ListItem>
                <b>Availability of Live Mentorship Service: </b>Live Mentorship
                Service will generally continue to be available to you until you
                completely consume the allotted live mentorship sessions to you
                during your Iconic Subscription Period. You may download and
                store your own copy of the Live mentorship session on a
                Compatible Device authorized for such in app download so that
                you can view that live mentorship session but the access to such
                in-app downloaded live mentorship session shall be removed on
                expiry of your Iconic Subscription Period or termination of your
                Iconic Subscription in accordance with the terms of this
                Agreement.
              </ListItem>
              <ListItem>
                <b>Quality of Streaming: </b>The resolution and quality of the
                live mentorship sessions will depend on a number of factors,
                including the type of Compatible Device on which you are
                availing the live mentorship services and your bandwidth. While
                we strive to provide you a high-quality experience, we make no
                guarantee as to the resolution or quality of the live mentorship
                sessions when streaming.
              </ListItem>
              <ListItem>
                <b>Conduct during Live Mentorship session: </b>You shall conduct
                yourself in a professional and respectful manner. You shall use
                the Live Mentorship services only for the purpose for which it
                is being provided and your conduct shall be in accordance with
                all the terms and conditions and policies mentioned on the
                Unacademy Platform, including but not limited to User
                guidelines, Unacademy online terms and conditions. Violation of
                any of our terms and conditions, policies and guidelines may
                result in suspense, termination of your account and also, we
                reserve the right to initiate any action against you that we
                deem fit.
              </ListItem>
              <ListItem>
                <b>General Restrictions: </b>You may not (i) transfer, copy or
                display the live mentorship session, except as permitted in this
                Agreement; (ii) sell, rent, lease, distribute, or broadcast any
                right to the live mentorship session; (iii) remove any
                proprietary notices or labels on the live mentorship sessions;
                (iv) attempt to disable, bypass, modify, defeat, or otherwise
                circumvent any digital rights management or other content
                protection system used as part of the Subscription; or (v) use
                the Iconic Subscription for any commercial or illegal purpose.
              </ListItem>
            </ol>
            <br />
            <br />

            <a name={tableData[3].id}>
              <StyledH3>{tableData[3].label}</StyledH3>
            </a>
            <ol>
              <ListItem>
                <b>Communications: </b>We may send you promotions or otherwise
                communicate with you electronically, which may include email,
                push notification, and you hereby consent to receive those
                communications. These communications will be in accordance with
                the Unacademy Privacy Policy.
              </ListItem>
              <ListItem>
                <b>Modification of Live Monitoring Services: </b>Unacademy
                reserves the right to modify, suspend, or discontinue the Live
                Mentorship Service, or any part of the Live Mentorship Service,
                at any time and without notice (except as required by applicable
                law), and Unacademy will not be liable to you should it exercise
                such rights.
              </ListItem>
              <ListItem>
                <b>Amendments: </b>Unacademy reserves the right to make changes
                to this Agreement at any time by posting the revised terms in
                connection with the Live Mentorship Service. To the maximum
                extent permitted by law, your continued use of the Live
                Mentorship Service following any changes will constitute your
                acceptance of such changes.
              </ListItem>
              <ListItem>
                <b>Reservation of Rights; Waiver: </b>The Live Mentorship
                Service embodies intellectual property that is protected by law.
                Copyright owners of live mentorship service, i.e. mentors, are
                intended third-party beneficiaries under the Agreement. Our
                failure to insist upon or enforce your strict compliance with
                the Agreement will not constitute a waiver of any of our rights.
              </ListItem>
              <ListItem>
                <b>Disputes/Conditions of Use: </b>Any dispute or claim arising
                from or relating to this Agreement or the Live Mentorship
                Service is subject to the governing law, disclaimer of
                warranties and limitation of liability, any binding arbitration,
                and all other terms in the Unacademy Terms of Use. You agree to
                those terms by using the Live Mentorship Service.
              </ListItem>
              <ListItem>
                <b>Limitation of Liability: </b>Without limiting the disclaimer
                of warranties and limitation of liability in the Unacademy Terms
                of Use, in no event shall Unacademy be liability to you for all
                damages arising out of or related to your use or inability to
                use the Live Mentorship Service; The limitations in this section
                will apply to you even if the remedies fail of their essential
                purpose.
              </ListItem>
              <ListItem>
                <b>Contact Information: </b>For communications concerning this
                Agreement, please write to Unacademy at{' '}
                <a href="mailto:help@unacademy.com">help@unacademy.com</a>.
              </ListItem>
              <ListItem>
                <b>Severability: </b>If any term or condition of this Agreement
                is deemed invalid, void, or for any reason unenforceable, that
                part will be deemed severable and will not affect the validity
                and enforceability of any remaining term or condition.
              </ListItem>
            </ol>
            <br />
            <br />
          </div>
        </Wrapper>
      </RightContainer>
    </>
  );
};

LiveMentorshipTerms.pageInfo = {
  name: 'Unacademy Live Mentorship Terms',
  isMWebSupported: true,
  layoutOptions: {
    hideGoalSelector: true,
    disableDarkMode: true
  }
};

export default LiveMentorshipTerms;
LiveMentorshipTerms.Layout = Layout;
LiveMentorshipTerms.Wrapper = PageReduxProvider;
